/**
 * UI Controls for the Pisa Tower simulation
 */

import type { Controls as IControls, SimulationParams } from '../types';

export class Controls implements IControls {
  private params: SimulationParams;
  private paramChangeCallbacks: Array<(params: Partial<SimulationParams>) => void> = [];
  private playCallbacks: Array<() => void> = [];
  private pauseCallbacks: Array<() => void> = [];
  private resetCallbacks: Array<() => void> = [];

  private playPauseBtn: HTMLButtonElement;
  private resetBtn: HTMLButtonElement;
  private initialBtn: HTMLButtonElement;
  private isPlaying = false;

  constructor() {
    // Initialize parameters from DOM
    this.params = {
      blueMass: this.getSliderValue('blueMass'),
      blueDensity: this.getSliderValue('blueDensity'),
      redMass: this.getSliderValue('redMass'),
      redDensity: this.getSliderValue('redDensity'),
      gravity: this.getSliderValue('gravity'),
      airDensity: this.getSliderValue('airDensity'),
      timeScale: this.getSliderValue('timeScale')
    };

    // Get button elements
    this.playPauseBtn = document.getElementById('playPauseBtn') as HTMLButtonElement;
    this.resetBtn = document.getElementById('resetBtn') as HTMLButtonElement;
    this.initialBtn = document.getElementById('initialBtn') as HTMLButtonElement;

    // Set up event listeners
    this.setupSliders();
    this.setupButtons();
    this.updateAllDisplays();
  }

  private getSliderValue(id: string): number {
    const slider = document.getElementById(id) as HTMLInputElement;
    return parseFloat(slider.value);
  }

  /**
   * Calculate diameter from mass and density
   * mass = density * volume = density * (4/3) * π * radius³
   * diameter = 2 * (3 * mass / (4 * π * density))^(1/3)
   */
  private calculateDiameter(mass: number, density: number): number {
    return 2 * Math.pow((3 * mass) / (4 * Math.PI * density), 1/3);
  }

  private setupSliders(): void {
    // Blue ball controls
    this.setupSlider('blueMass', (value) => {
      this.updateDisplay('blueMassValue', `${value.toFixed(1)} kg`);
      const diameter = this.calculateDiameter(value, this.params.blueDensity);
      this.updateDisplay('blueDiameterValue', `${diameter.toFixed(2)} m`);
      this.notifyParamChange({ blueMass: value });
    });

    this.setupSlider('blueDensity', (value) => {
      this.updateDisplay('blueDensityValue', `${Math.round(value)} kg/m³`);
      const diameter = this.calculateDiameter(this.params.blueMass, value);
      this.updateDisplay('blueDiameterValue', `${diameter.toFixed(2)} m`);
      this.notifyParamChange({ blueDensity: value });
    });

    // Red ball controls
    this.setupSlider('redMass', (value) => {
      this.updateDisplay('redMassValue', `${value.toFixed(1)} kg`);
      const diameter = this.calculateDiameter(value, this.params.redDensity);
      this.updateDisplay('redDiameterValue', `${diameter.toFixed(2)} m`);
      this.notifyParamChange({ redMass: value });
    });

    this.setupSlider('redDensity', (value) => {
      this.updateDisplay('redDensityValue', `${Math.round(value)} kg/m³`);
      const diameter = this.calculateDiameter(this.params.redMass, value);
      this.updateDisplay('redDiameterValue', `${diameter.toFixed(2)} m`);
      this.notifyParamChange({ redDensity: value });
    });

    // Environment controls
    this.setupSlider('gravity', (value) => {
      this.updateDisplay('gravityValue', `${value.toFixed(2)} m/s²`);
      this.notifyParamChange({ gravity: value });
    });

    this.setupSlider('airDensity', (value) => {
      this.updateDisplay('airDensityValue', `${value.toFixed(3)} kg/m³`);
      this.notifyParamChange({ airDensity: value });
    });

    this.setupSlider('timeScale', (value) => {
      this.updateDisplay('timeScaleValue', `${value.toFixed(2)}×`);
      this.notifyParamChange({ timeScale: value });
    });
  }

  private setupSlider(id: string, onChange: (value: number) => void): void {
    const slider = document.getElementById(id) as HTMLInputElement;
    slider.addEventListener('input', () => {
      const value = parseFloat(slider.value);
      onChange(value);
    });
  }

  private isComplete = false;

  private setupButtons(): void {
    this.playPauseBtn.addEventListener('click', () => {
      // If simulation is complete, auto-reset before playing
      if (this.isComplete) {
        this.resetCallbacks.forEach(cb => cb());
        this.isComplete = false;
      }

      if (this.isPlaying) {
        this.isPlaying = false;
        this.playPauseBtn.textContent = 'Play';
        this.pauseCallbacks.forEach(cb => cb());
      } else {
        this.isPlaying = true;
        this.playPauseBtn.textContent = 'Pause';
        this.playCallbacks.forEach(cb => cb());
      }
    });

    this.resetBtn.addEventListener('click', () => {
      this.isPlaying = false;
      this.playPauseBtn.textContent = 'Play';
      this.resetCallbacks.forEach(cb => cb());
    });

    this.initialBtn.addEventListener('click', () => {
      this.resetToInitialValues();
      this.isPlaying = false;
      this.playPauseBtn.textContent = 'Play';
      this.resetCallbacks.forEach(cb => cb());
    });
  }

  private resetToInitialValues(): void {
    const sliderIds = ['blueMass', 'blueDensity', 'redMass', 'redDensity', 'gravity', 'airDensity', 'timeScale'];

    for (const id of sliderIds) {
      const slider = document.getElementById(id) as HTMLInputElement;
      const initialValue = slider.getAttribute('data-initial');
      if (initialValue) {
        slider.value = initialValue;
        // Trigger the input event to update displays and params
        slider.dispatchEvent(new Event('input'));
      }
    }
  }

  private updateDisplay(id: string, value: string): void {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = value;
    }
  }

  private updateAllDisplays(): void {
    // Blue ball
    this.updateDisplay('blueMassValue', `${this.params.blueMass.toFixed(1)} kg`);
    this.updateDisplay('blueDensityValue', `${Math.round(this.params.blueDensity)} kg/m³`);
    const blueDiameter = this.calculateDiameter(this.params.blueMass, this.params.blueDensity);
    this.updateDisplay('blueDiameterValue', `${blueDiameter.toFixed(2)} m`);

    // Red ball
    this.updateDisplay('redMassValue', `${this.params.redMass.toFixed(1)} kg`);
    this.updateDisplay('redDensityValue', `${Math.round(this.params.redDensity)} kg/m³`);
    const redDiameter = this.calculateDiameter(this.params.redMass, this.params.redDensity);
    this.updateDisplay('redDiameterValue', `${redDiameter.toFixed(2)} m`);

    // Environment
    this.updateDisplay('gravityValue', `${this.params.gravity.toFixed(2)} m/s²`);
    this.updateDisplay('airDensityValue', `${this.params.airDensity.toFixed(3)} kg/m³`);
    this.updateDisplay('timeScaleValue', `${this.params.timeScale.toFixed(2)}×`);
  }

  private notifyParamChange(params: Partial<SimulationParams>): void {
    // Update internal params
    Object.assign(this.params, params);

    // Notify callbacks
    this.paramChangeCallbacks.forEach(cb => cb(params));
  }

  onParamChange(callback: (params: Partial<SimulationParams>) => void): void {
    this.paramChangeCallbacks.push(callback);
  }

  onPlay(callback: () => void): void {
    this.playCallbacks.push(callback);
  }

  onPause(callback: () => void): void {
    this.pauseCallbacks.push(callback);
  }

  onReset(callback: () => void): void {
    this.resetCallbacks.push(callback);
  }

  updatePlayButton(playing: boolean, complete: boolean): void {
    this.isPlaying = playing;
    this.isComplete = complete;
    this.playPauseBtn.textContent = playing ? 'Pause' : 'Play';
    // Never disable the button - it's always available
  }

  getParams(): SimulationParams {
    return { ...this.params };
  }
}
